export const topologies = [

  // ✅ Induction Machine
  {
    topology: "Induction Machine",
    groups: [
      {
        name: "Supply Specification",
        parameters: [
          {
            parameter: "Phase number",
            key: "phase_number",
            defaultValue: 3,
            description: "Number of phases of the machine.",
            type: "number",
          },
          {
            parameter: "Voltage definition",
            key: "voltage_definition",
            defaultValue: "RMS",
            description:
              "Defines whether the supply voltage is given as RMS or Peak.",
            type: "option",
            options: ["RMS", "Peak"],
          },
          {
            parameter: "Winding connection",
            key: "winding_connection",
            defaultValue: "Star",
            description: "Selects the winding configuration (Star or Delta).",
            type: "option",
            options: ["Star", "Delta"],
          },
          {
            parameter: "Supply voltage [V]",
            key: "supply_voltage",
            defaultValue: 100.0,
            description: "Electrical supply voltage in volts.",
            type: "number",
          },
          {
            parameter: "Supply frequency [Hz]",
            key: "supply_frequency",
            defaultValue: 50.0,
            description: "Electrical supply frequency in hertz.",
            type: "number",
          },
        ],
      },

      {
        name: "Stator Specification",
        parameters: [
          {
            parameter: "Number of slots",
            key: "number_of_stator_slots",
            defaultValue: 36,
            description: "Total number of stator slots.",
            type: "number",
          },
          {
            parameter: "Number of pole pairs",
            key: "number_of_pole_pairs",
            defaultValue: 2,
            description:
              "Number of machine pole pairs (half the total pole count).",
            type: "number",
          },
          {
            parameter: "Outer diameter [mm]",
            key: "stator_outer_diameter",
            defaultValue: 300.0,
            description: "Outer diameter of the stator in millimeters.",
            type: "number",
          },
          {
            parameter: "Stator inner diameter [mm]",
            key: "air_gap_diameter",
            defaultValue: 200.0,
            description: "Stator inner diameter in millimeters.",
            type: "number",
          },
          {
            parameter: "Stack length [mm]",
            key: "stack_length",
            defaultValue: 150,
            description:
              "Axial length of the stator/rotor stack in millimeters.",
            type: "number",
          },

          // ✅ slot shape selector
          {
            parameter: "Slot shape",
            key: "stator_slot_shape",
            defaultValue: "Rectangle",
            description: "Shape of the stator slots.",
            type: "option",
            options: ["Rectangle", "Trapezoidal", "Drop profile"],
          },

          // ✅ RULES
          // Rectangle -> show slot width; hide top/bottom
          // Trapezoidal & Drop profile -> show top/bottom; hide slot width
          {
            parameter: "Slot width [mm]",
            key: "stator_slot_width",
            defaultValue: 10,
            description: "Width of the stator slot in millimeters.",
            type: "number",
            visibleWhen: { field: "stator_slot_shape", equals: "Rectangle" },
          },
          {
            parameter: "Slot width (top) [mm]",
            key: "stator_slot_width_top",
            defaultValue: 10,
            description: "Top width of the stator slot in millimeters.",
            type: "number",
            visibleWhen: {
              field: "stator_slot_shape",
              in: ["Trapezoidal", "Drop profile"],
            },
          },
          {
            parameter: "Slot width (bottom) [mm]",
            key: "stator_slot_width_bottom",
            defaultValue: 8,
            description: "Bottom width of the stator slot in millimeters.",
            type: "number",
            visibleWhen: {
              field: "stator_slot_shape",
              in: ["Trapezoidal", "Drop profile"],
            },
          },

          {
            parameter: "Slot height [mm]",
            key: "stator_slot_height",
            defaultValue: 17,
            description: "Height of the stator slot in millimeters.",
            type: "number",
          },
          {
            parameter: "Slot opening height [mm]",
            key: "stator_slot_opening_height",
            defaultValue: 0.96,
            description: "Height of the stator slot opening in millimeters.",
            type: "number",
          },
          {
            parameter: "Tooth tip angle [deg]",
            key: "stator_tooth_tip_angle",
            defaultValue: 20.0,
            description: "Angle of the stator tooth tip in degrees.",
            type: "number",
          },
          {
            parameter: "Slot opening width [mm]",
            key: "stator_slot_opening_width",
            defaultValue: 3,
            description: "Width of the stator slot opening in millimeters.",
            type: "number",
          },
          {
            parameter: "Stator skew",
            key: "stator_skew",
            defaultValue: false,
            description: "Indicates whether the stator is skewed.",
            type: "boolean",
          },
        ],
      },

      {
        name: "Stator Winding Specification",
        parameters: [
          {
            parameter: "Number of layers",
            key: "stator_winding_layer",
            defaultValue: 1,
            description: "Number of winding layers.",
            type: "option",
            options: ["1", "2"],
          },
          {
            parameter: "Turns per coil",
            key: "stator_turns_per_coil",
            defaultValue: 10,
            description: "Number of turns per winding coil.",
            type: "number",
          },
          {
            parameter: "Number of parallel strands",
            key: "stator_number_of_parallel_strands",
            defaultValue: 1,
            description: "Number of parallel strands in the winding.",
            type: "number",
          },
          {
            parameter: "Stator slot copper fill factor",
            key: "stator_slot_copper_fill_factor",
            defaultValue: 0.45,
            description: "Copper fill factor of the stator slot winding.",
            type: "number",
          },
          {
            parameter: "Winding parallel paths",
            key: "stator_winding_parallel_paths",
            defaultValue: 1,
            description: "Number of parallel paths in the stator winding.",
            type: "number",
          },
          {
            parameter: "Winding end length [mm]",
            key: "stator_winding_end_length",
            defaultValue: 80,
            description:
              "Length of the winding end connections in millimeters.",
            type: "number",
          },
          // {
          //   parameter: "Coil Throw",
          //   key: "coil_throw",
          //   defaultValue: 1,
          //   description:
          //     "The coil throw, or span, indicating how many slots a coil spans. This value is automatically calculated but can be manually adjusted within its maximum determined value.",
          //   type: "number",
          // },
          {
            parameter: "Coil Throw",
            key: "coil_throw",
            // defaultValue: 1,
            description:
              "The coil throw, or span, indicating how many slots a coil spans. This value is automatically calculated but can be manually adjusted within its maximum determined value.",
            // type: "number",
            type: "computed",
            dependsOn: ["number_of_stator_slots", "number_of_pole_pairs"],
            // formula: (p: Record<string, number>) =>
            //   Math.round(p["number_of_stator_slots"] / (2 * p["number_of_pole_pairs"])),
          },
          {
            parameter: "Winding temperature [°C]",
            key: "stator_winding_temperature",
            defaultValue: 40.0,
            description: "Temperature of the stator winding in °C.",
            type: "number",
          },
          {
            parameter: "Winding material",
            key: "stator_winding_material",
            defaultValue: "Copper",
            description: "Material of the winding (Aluminium or Copper).",
            type: "option",
            options: ["Aluminium", "Copper"],
          },
          {
            parameter: "Winding type",
            key: "winding_type",
            defaultValue: "Distributed",
            description: "Type of winding arrangement.",
            type: "option",
            options: ["Distributed"]
          },         
        ],
      },

      {
        name: "Rotor Specification",
        parameters: [
          // ✅ General geometry (always visible)
          {
            parameter: "Number of slots",
            key: "number_of_rotor_slots",
            defaultValue: 24,
            description: "Total number of rotor slots.",
            type: "number",
          },
          {
            parameter: "Outer diameter [mm]",
            key: "rotor_outer_diameter",
            defaultValue: 199,
            description: "Rotor outer diameter in millimeters.",
            type: "number",
          },
          {
            parameter: "Shaft diameter [mm]",
            key: "rotor_inner_diameter",
            defaultValue: 50,
            description: "Diameter of the rotor shaft in millimeters.",
            type: "number",
          },
          {
            parameter: "Rotor skew",
            key: "rotor_skew",
            defaultValue: false,
            description: "Indicates whether the rotor is skewed.",
            type: "boolean",
          },

          // ✅ rotor type selector (always visible)
          {
            parameter: "Rotor type",
            key: "rotor_type",
            defaultValue: "Squirrel Cage",
            description: "Type of rotor (Squirrel Cage or Wound Field).",
            type: "option",
            options: ["Squirrel Cage", "Wound Field"],
          },

          // ✅ cage type only when squirrel cage
          {
            parameter: "Cage type",
            key: "cage_type",
            defaultValue: "Single Cage",
            description:
              "Cage type for Squirrel Cage rotors (Single or Double).",
            type: "option",
            options: ["Single Cage", "Double Cage"],
            visibleWhen: { field: "rotor_type", equals: "Squirrel Cage" },
          },

          // ✅ Single Cage shape only when squirrel + single
          {
            parameter: "Slot shape",
            key: "rotor_slot_shape_single_cage",
            defaultValue: "Rectangle",
            description: "Single Cage rotor slot shape.",
            type: "option",
            options: [
              "Rectangle",
              "Trapezoidal",
              "Drop profile",
              "Pear",
              "Circular",
            ],
            visibleWhen: {
              all: [
                { field: "rotor_type", equals: "Squirrel Cage" },
                { field: "cage_type", equals: "Single Cage" },
              ],
            },
          },

          // ✅ wound rotor shape only when wound field
          {
            parameter: "Slot shape",
            key: "rotor_slot_shape_wound_rotor",
            defaultValue: "Rectangle",
            description: "Wound Field slot shape.",
            type: "option",
            options: ["Rectangle", "Trapezoidal", "Drop profile", "Circular"],
            visibleWhen: { field: "rotor_type", equals: "Wound Field" },
          },

          // ✅ Double Cage shapes only when squirrel + double
          // When Double Cage, only "Circular" is allowed for upper cage (restrictOptionsWhen)
          {
            parameter: "Rotor slot shape upper cage",
            key: "rotor_slot_shape_upper_cage",
            defaultValue: "Circular",
            description:
              "Shape of the rotor slot for the upper cage in a Double Cage rotor.",
            type: "option",
            options: [
              "Rectangle",
              "Trapezoidal",
              "Drop profile",
              "Pear",
              "Circular",
            ],
            visibleWhen: {
              all: [
                { field: "rotor_type", equals: "Squirrel Cage" },
                { field: "cage_type", equals: "Double Cage" },
              ],
            },
            restrictOptionsWhen: {
              when: { field: "cage_type", equals: "Double Cage" },
              options: ["Circular"],
            },
          },
          {
            parameter: "Rotor slot shape lower cage",
            key: "rotor_slot_shape_lower_cage",
            defaultValue: "Rectangle",
            description:
              "Shape of the rotor slot for the lower cage in a Double Cage rotor.",
            type: "option",
            options: [
              "Rectangle",
              "Trapezoidal",
              "Drop profile",
              "Pear",
              "Circular",
            ],
            visibleWhen: {
              all: [
                { field: "rotor_type", equals: "Squirrel Cage" },
                { field: "cage_type", equals: "Double Cage" },
              ],
            },
            restrictOptionsWhen: {
              when: { field: "cage_type", equals: "Double Cage" },
              options: [
                "Rectangle",
                "Trapezoidal",
                "Drop profile",
                "Pear",
              ],
            },
          },

          // ✅ ring topology only when squirrel + double
          {
            parameter: "Ring topology",
            key: "ring_topology",
            defaultValue: "Separate Ring",
            description:
              "End ring topology for Double Cage rotors (Separate or Common).",
            type: "option",
            options: ["Separate Ring", "Common Ring"],
            visibleWhen: {
              all: [
                { field: "rotor_type", equals: "Squirrel Cage" },
                { field: "cage_type", equals: "Double Cage" },
              ],
            },
          },

          // ✅ Open slot only relevant for circular/pear (Single Cage) OR circular/pear (upper/lower in Double Cage)
          {
            parameter: "Open slot",
            key: "open_rotor_slot",
            defaultValue: true,
            description:
              "Indicates whether the rotor slot is open (for Pear or Circular shapes).",
            type: "boolean",
            visibleWhen: {
              any: [
                {
                  all: [
                    { field: "rotor_type", equals: "Squirrel Cage" },
                    { field: "cage_type", equals: "Single Cage" },
                    {
                      field: "rotor_slot_shape_single_cage",
                      in: ["Pear", "Circular"],
                    },
                  ],
                },
                {
                  all: [
                    { field: "rotor_type", equals: "Squirrel Cage" },
                    { field: "cage_type", equals: "Double Cage" },
                    {
                      field: "rotor_slot_shape_upper_cage",
                      in: ["Pear", "Circular"],
                    },
                  ],
                },
                {
                  all: [
                    { field: "rotor_type", equals: "Squirrel Cage" },
                    { field: "cage_type", equals: "Double Cage" },
                    {
                      field: "rotor_slot_shape_lower_cage",
                      in: ["Pear", "Circular"],
                    },
                  ],
                },
              ],
            },
            valueWhen: {
              when: { field: "cage_type", equals: "Double Cage" },
              value: true
            },
          },

          // Tooth tip angles (rectangle/trapezoidal/Drop profiles)
          {
            parameter: "Tooth tip angle [deg]",
            key: "rotor_tooth_tip_angle",
            defaultValue: 20.0,
            description:
              "Angle of the rotor tooth tip (Rectangle and Trapezoidal slot) in degrees.",
            type: "number",
            visibleWhen: {
              any: [
                {
                  all: [
                    { field: "rotor_type", equals: "Squirrel Cage" },
                    { field: "cage_type", equals: "Single Cage" },
                    {
                      field: "rotor_slot_shape_single_cage",
                      in: ["Rectangle", "Trapezoidal", "Drop profile"],
                    },
                  ],
                },
                {
                  all: [
                    { field: "rotor_type", equals: "Wound Field" },
                    {
                      field: "rotor_slot_shape_wound_rotor",
                      in: ["Rectangle", "Trapezoidal", "Drop profile"],
                    },
                  ],
                },
              ],
            },
          },
          // --- Single Cage / WOUND FIELD rectangle-only width
          {
            parameter: "Slot width [mm]",
            key: "rotor_slot_width",
            defaultValue: 10,
            description: "Width of the rotor slot in millimeters.",
            type: "number",
            visibleWhen: {
              any: [
                {
                  all: [
                    { field: "rotor_type", equals: "Squirrel Cage" },
                    { field: "cage_type", equals: "Single Cage" },
                    {
                      field: "rotor_slot_shape_single_cage",
                      equals: "Rectangle",
                    },
                  ],
                },
                {
                  all: [
                    { field: "rotor_type", equals: "Wound Field" },
                    {
                      field: "rotor_slot_shape_wound_rotor",
                      equals: "Rectangle",
                    },
                  ],
                },
              ],
            },
          },

          // --- Single Cage / WOUND FIELD trapezoidal/drop => top/bottom
          {
            parameter: "Slot width (top) [mm]",
            key: "rotor_slot_width_top",
            defaultValue: 10,
            description: "Top width of the rotor slot in millimeters.",
            type: "number",
            visibleWhen: {
              any: [
                {
                  all: [
                    { field: "rotor_type", equals: "Squirrel Cage" },
                    { field: "cage_type", equals: "Single Cage" },
                    {
                      field: "rotor_slot_shape_single_cage",
                      in: ["Trapezoidal", "Drop profile", "Pear"],
                    },
                  ],
                },
                {
                  all: [
                    { field: "rotor_type", equals: "Wound Field" },
                    {
                      field: "rotor_slot_shape_wound_rotor",
                      in: ["Trapezoidal", "Drop profile"],
                    },
                  ],
                },
              ],
            },
          },
          {
            parameter: "Slot width (bottom) [mm]",
            key: "rotor_slot_width_bottom",
            defaultValue: 8,
            description: "Bottom width of the rotor slot in millimeters.",
            type: "number",
            visibleWhen: {
              any: [
                {
                  all: [
                    { field: "rotor_type", equals: "Squirrel Cage" },
                    { field: "cage_type", equals: "Single Cage" },
                    {
                      field: "rotor_slot_shape_single_cage",
                      in: ["Trapezoidal", "Drop profile", "Pear"],
                    },
                  ],
                },
                {
                  all: [
                    { field: "rotor_type", equals: "Wound Field" },
                    {
                      field: "rotor_slot_shape_wound_rotor",
                      in: ["Trapezoidal", "Drop profile"],
                    },
                  ],
                },
              ],
            },
          },  
          // Slot opening (Single Cage rectangle/trap/drop) OR Wound field rectangle/trap/drop
          // ✅ FIX: unique key (Single Cage / wound field)
          {
            parameter: "Slot opening width [mm]",
            key: "rotor_slot_opening_width",
            defaultValue: 2,
            description:
              "Width of the rotor slot opening (if open) in millimeters.",
            type: "number",
            visibleWhen: {
              any: [
                {
                  all: [
                    { field: "rotor_type", equals: "Squirrel Cage" },
                    { field: "cage_type", equals: "Double Cage" },
                  ],
                },
                {
                  all: [
                    { field: "rotor_type", equals: "Squirrel Cage" },
                    { field: "cage_type", equals: "Single Cage" },
                    {
                      field: "rotor_slot_shape_single_cage",
                      in: ["Rectangle", "Trapezoidal", "Drop profile"],
                    },
                  ],
                },
                {
                  all: [
                    { field: "rotor_type", equals: "Wound Field" },
                    {
                      field: "rotor_slot_shape_wound_rotor",
                      in: [
                        "Rectangle",
                        "Trapezoidal",
                        "Drop profile",
                        "Circular",
                      ],
                    },
                  ],
                },
                // Circular/Pear Single Cage when open slot = true
                {
                  all: [
                    { field: "rotor_type", equals: "Squirrel Cage" },
                    { field: "cage_type", equals: "Single Cage" },
                    {
                      field: "rotor_slot_shape_single_cage",
                      in: ["Circular", "Pear"],
                    },
                    { field: "open_rotor_slot", equals: true },
                  ],
                },
              ],
            },
          },
          {
            parameter: "Slot opening height [mm]",
            key: "rotor_slot_opening_height",
            defaultValue: 2,
            description:
              "Height of the rotor slot opening (if open) in millimeters.",
            type: "number",
            visibleWhen: {
              any: [
                {
                  all: [
                    { field: "rotor_type", equals: "Squirrel Cage" },
                    { field: "cage_type", equals: "Single Cage" },
                    {
                      field: "rotor_slot_shape_single_cage",
                      in: ["Rectangle", "Trapezoidal", "Drop profile"],
                    },
                  ],
                },
                {
                  all: [
                    { field: "rotor_type", equals: "Wound Field" },
                    {
                      field: "rotor_slot_shape_wound_rotor",
                      in: [
                        "Rectangle",
                        "Trapezoidal",
                        "Drop profile",
                        "Circular",
                      ],
                    },
                  ],
                },
                // Circular/Pear Single Cage when open slot = true
                {
                  all: [
                    { field: "rotor_type", equals: "Squirrel Cage" },
                    { field: "cage_type", equals: "Single Cage" },
                    {
                      field: "rotor_slot_shape_single_cage",
                      in: ["Circular", "Pear"],
                    },
                    { field: "open_rotor_slot", equals: true },
                  ],
                },
              ],
            },
          }, 
          {
            parameter: "Slot height [mm]",
            key: "rotor_slot_height",
            defaultValue: 15,
            description: "Height of the rotor slot in millimeters.",
            type: "number",
            visibleWhen: {
              any: [
                {
                  all: [
                    { field: "rotor_type", equals: "Squirrel Cage" },
                    { field: "cage_type", equals: "Single Cage" },
                    {
                      field: "rotor_slot_shape_single_cage",
                      in: ["Rectangle", "Trapezoidal", "Drop profile", "Pear"],
                    },
                  ],
                },
                {
                  all: [
                    { field: "rotor_type", equals: "Wound Field" },
                    {
                      field: "rotor_slot_shape_wound_rotor",
                      in: ["Rectangle", "Trapezoidal", "Drop profile"],
                    },
                  ],
                },
                // Circular/Pear Single Cage when open slot = true
                {
                  all: [
                    { field: "rotor_type", equals: "Squirrel Cage" },
                    { field: "cage_type", equals: "Single Cage" },
                    {
                      field: "rotor_slot_shape_single_cage",
                      in: ["Circular", "Pear"],
                    },
                    { field: "open_rotor_slot", equals: true },
                  ],
                },
              ],
            },
          },  
          {
            parameter: "Slot diameter [mm]",
            key: "rotor_slot_diameter",
            defaultValue: 10,
            description:
              "Diameter of the rotor slot (Circular shape) in millimeters.",
            type: "number",
            visibleWhen: {
              any: [
                {
                  all: [
                    { field: "rotor_type", equals: "Squirrel Cage" },
                    { field: "cage_type", equals: "Single Cage" },
                    {
                      field: "rotor_slot_shape_single_cage",
                      equals: "Circular",
                    },
                  ],
                },
                {
                  all: [
                    { field: "rotor_type", equals: "Wound Field" },
                    {
                      field: "rotor_slot_shape_wound_rotor",
                      equals: "Circular",
                    },
                  ],
                },
              ],
            },
          },       
          // Iron bridge height shown only when Circular/Pear and open slot = false (Single Cage)
          {
            parameter: "Iron bridge height [mm]",
            key: "iron_bridge_height",
            defaultValue: 2,
            description:
              "Height of the iron bridge (for closed rotor slots) in millimeters.",
            type: "number",
            visibleWhen: {
              all: [
                { field: "rotor_type", equals: "Squirrel Cage" },
                { field: "cage_type", equals: "Single Cage" },
                {
                  field: "rotor_slot_shape_single_cage",
                  in: ["Circular", "Pear"],
                },
                { field: "open_rotor_slot", equals: false },
              ],
            },
          },  
          // ✅ End-ring rules:
          // - Single Cage: show common end ring (height/width/extension)
          // - Double Cage + Separate Ring: show upper+lower end ring dims
          // - Double Cage + Common Ring: show common end ring dims
          {
            parameter: "End-ring height [mm]",
            key: "end_ring_height",
            defaultValue: 21,
            description: "Height of the rotor end-ring in millimeters.",
            type: "number",
            visibleWhen: {
              any: [
                {
                  all: [
                    { field: "rotor_type", equals: "Squirrel Cage" },
                    { field: "cage_type", equals: "Single Cage" },
                  ],
                },
                {
                  all: [
                    { field: "rotor_type", equals: "Squirrel Cage" },
                    { field: "cage_type", equals: "Double Cage" },
                    { field: "ring_topology", equals: "Common Ring" },
                  ],
                },
              ],
            },
          },
          {
            parameter: "End-ring width [mm]",
            key: "end_ring_width",
            defaultValue: 20,
            description: "Width of the rotor end-ring in millimeters.",
            type: "number",
            visibleWhen: {
              any: [
                {
                  all: [
                    { field: "rotor_type", equals: "Squirrel Cage" },
                    { field: "cage_type", equals: "Single Cage" },
                  ],
                },
                {
                  all: [
                    { field: "rotor_type", equals: "Squirrel Cage" },
                    { field: "cage_type", equals: "Double Cage" },
                    { field: "ring_topology", equals: "Common Ring" },
                  ],
                },
              ],
            },
          },
          {
            parameter: "End-ring extension [mm]",
            key: "end_ring_extension",
            defaultValue: 0,
            description: "Extension of the rotor end-ring in millimeters.",
            type: "number",
            visibleWhen: {
              any: [
                {
                  all: [
                    { field: "rotor_type", equals: "Squirrel Cage" },
                    { field: "cage_type", equals: "Single Cage" },
                  ],
                },
                {
                  all: [
                    { field: "rotor_type", equals: "Squirrel Cage" },
                    { field: "cage_type", equals: "Double Cage" },
                    { field: "ring_topology", equals: "Common Ring" },
                  ],
                },
              ],
            },
          },
                             
          // ✅ Double Cage // Upper Cage Data // (UI: Divider before this block — see inductionDoubleCageRotorDividers.ts + Layout / EditProjectModal)
          {
            parameter: "Tooth tip angle upper cage [deg]",
            key: "rotor_tooth_tip_angle_upper",
            defaultValue: 20.0,
            description:
              "Angle of the rotor tooth tip (Rectangle and Trapezoidal slot) in degrees for the upper cage in a Double Cage rotor.",
            type: "number",
            visibleWhen: {
              all: [
                { field: "rotor_type", equals: "Squirrel Cage" },
                { field: "cage_type", equals: "Double Cage" },
                {
                  field: "rotor_slot_shape_upper_cage",
                  in: ["Rectangle", "Trapezoidal", "Drop profile"],
                },
              ],
            },
          },
          // --- Double Cage: upper widths (top/bottom) and rectangle width
          {
            parameter: "Slot width upper cage (top) [mm]",
            key: "rotor_slot_width_top_upper",
            defaultValue: 10,
            description:
              "Top width of the rotor slot for the upper cage in millimeters.",
            type: "number",
            visibleWhen: {
              all: [
                { field: "rotor_type", equals: "Squirrel Cage" },
                { field: "cage_type", equals: "Double Cage" },
                {
                  field: "rotor_slot_shape_upper_cage",
                  in: ["Trapezoidal", "Drop profile", "Pear"],
                },
              ],
            },
          },
          {
            parameter: "Slot width upper cage (bottom) [mm]",
            key: "rotor_slot_width_bottom_upper",
            defaultValue: 8,
            description:
              "Bottom width of the rotor slot for the upper cage in millimeters.",
            type: "number",
            visibleWhen: {
              all: [
                { field: "rotor_type", equals: "Squirrel Cage" },
                { field: "cage_type", equals: "Double Cage" },
                {
                  field: "rotor_slot_shape_upper_cage",
                  in: ["Trapezoidal", "Drop profile", "Pear"],
                },
              ],
            },
          },
          {
            parameter: "Slot width upper cage [mm]",
            key: "rotor_slot_width_upper",
            defaultValue: 10,
            description:
              "Width of upper cage slot for Rectangle in millimeters.",
            type: "number",
            visibleWhen: {
              all: [
                { field: "rotor_type", equals: "Squirrel Cage" },
                { field: "cage_type", equals: "Double Cage" },
                { field: "rotor_slot_shape_upper_cage", equals: "Rectangle" },
              ],
            },
          },
          {  
            parameter: "Slot opening height upper cage [mm]",
            key: "rotor_slot_opening_height_upper",
            defaultValue: 2,
            description:
              "Height of the rotor slot opening (if open) for the upper cage in millimeters.",
            type: "number",
            visibleWhen: {
              all: [
                { field: "rotor_type", equals: "Squirrel Cage" },
                { field: "cage_type", equals: "Double Cage" },
                {
                  field: "rotor_slot_shape_upper_cage",
                  in: ["Rectangle", "Trapezoidal", "Drop profile", "Circular"],
                },
              ],
            },
          },  
          {
            parameter: "Slot height upper cage [mm]",
            key: "rotor_slot_height_upper",
            defaultValue: 15,
            description:
              "Height of the rotor slot for the upper cage in millimeters.",
            type: "number",
            visibleWhen: {
              all: [
                { field: "rotor_type", equals: "Squirrel Cage" },
                { field: "cage_type", equals: "Double Cage" },
                {
                  field: "rotor_slot_shape_upper_cage",
                  in: ["Rectangle", "Trapezoidal", "Drop profile", "Pear"],
                },
              ],
            },
          },      
          {
            parameter: "Slot diameter upper cage [mm]",
            key: "rotor_slot_diameter_upper",
            defaultValue: 10,
            description:
              "Diameter of the rotor slot (Circular shape) for the upper cage in millimeters.",
            type: "number",
            visibleWhen: {
              all: [
                { field: "rotor_type", equals: "Squirrel Cage" },
                { field: "cage_type", equals: "Double Cage" },
                { field: "rotor_slot_shape_upper_cage", equals: "Circular" },
              ],
            },
          },  
          {
            parameter: "End-ring height upper cage [mm]",
            key: "end_ring_height_upper",
            defaultValue: 21,
            description:
              "Height of the rotor end-ring for the upper cage in millimeters.",
            type: "number",
            visibleWhen: {
              all: [
                { field: "rotor_type", equals: "Squirrel Cage" },
                { field: "cage_type", equals: "Double Cage" },
                { field: "ring_topology", equals: "Separate Ring" },
              ],
            },
          },
          {
            parameter: "End-ring width upper cage [mm]",
            key: "end_ring_width_upper",
            defaultValue: 20,
            description:
              "Width of the rotor end-ring for the upper cage in millimeters.",
            type: "number",
            visibleWhen: {
              all: [
                { field: "rotor_type", equals: "Squirrel Cage" },
                { field: "cage_type", equals: "Double Cage" },
                { field: "ring_topology", equals: "Separate Ring" },
              ],
            },
          },
          {
            parameter: "End-ring extension upper cage [mm]",
            key: "end_ring_extension_upper",
            defaultValue: 0,
            description:
              "Extension of the rotor end-ring for the upper cage in millimeters.",
            type: "number",
            visibleWhen: {
              all: [
                { field: "rotor_type", equals: "Squirrel Cage" },
                { field: "cage_type", equals: "Double Cage" },
                { field: "ring_topology", equals: "Separate Ring" },
              ],
            },
          },
          // ✅ Double Cage // Lower Cage Data // (UI: Divider before this block — see inductionDoubleCageRotorDividers.ts + Layout / EditProjectModal)
          {
            parameter: "Tooth tip angle lower cage [deg]",
            key: "rotor_tooth_tip_angle_lower",
            defaultValue: 20.0,
            description:
              "Angle of the rotor tooth tip (Rectangle and Trapezoidal slot) in degrees for the lower cage in a Double Cage rotor.",
            type: "number",
            visibleWhen: {
              all: [
                { field: "rotor_type", equals: "Squirrel Cage" },
                { field: "cage_type", equals: "Double Cage" },
                {
                  field: "rotor_slot_shape_lower_cage",
                  in: ["Rectangle", "Trapezoidal", "Drop profile"],
                },
              ],
            },
          },
          // --- Double Cage: lower widths (top/bottom) and rectangle width
          {
            parameter: "Slot width lower cage (top) [mm]",
            key: "rotor_slot_width_top_lower",
            defaultValue: 10,
            description:
              "Top width of the rotor slot for the lower cage in millimeters.",
            type: "number",
            visibleWhen: {
              all: [
                { field: "rotor_type", equals: "Squirrel Cage" },
                { field: "cage_type", equals: "Double Cage" },
                {
                  field: "rotor_slot_shape_lower_cage",
                  in: ["Trapezoidal", "Drop profile", "Pear"],
                },
              ],
            },
          },
          {
            parameter: "Slot width lower cage (bottom) [mm]",
            key: "rotor_slot_width_bottom_lower",
            defaultValue: 8,
            description:
              "Bottom width of the rotor slot for the lower cage in millimeters.",
            type: "number",
            visibleWhen: {
              all: [
                { field: "rotor_type", equals: "Squirrel Cage" },
                { field: "cage_type", equals: "Double Cage" },
                {
                  field: "rotor_slot_shape_lower_cage",
                  in: ["Trapezoidal", "Drop profile", "Pear"],
                },
              ],
            },
          },
          {
            parameter: "Slot width lower cage [mm]",
            key: "rotor_slot_width_lower",
            defaultValue: 8,
            description:
              "Width of lower cage slot for Rectangle in millimeters.",
            type: "number",
            visibleWhen: {
              all: [
                { field: "rotor_type", equals: "Squirrel Cage" },
                { field: "cage_type", equals: "Double Cage" },
                { field: "rotor_slot_shape_lower_cage", equals: "Rectangle" },
              ],
            },
          },
          {
            parameter: "Slot opening height lower cage [mm]",
            key: "rotor_slot_opening_height_lower",
            defaultValue: 2,
            description:
              "Height of the rotor slot opening (if open) for the lower cage in millimeters.",
            type: "number",
            visibleWhen: {
              all: [
                { field: "rotor_type", equals: "Squirrel Cage" },
                { field: "cage_type", equals: "Double Cage" },
                {
                  field: "rotor_slot_shape_lower_cage",
                  in: ["Rectangle", "Trapezoidal", "Drop profile"],
                },
              ],
            },
          },
          {
            parameter: "Slot height lower cage [mm]",
            key: "rotor_slot_height_lower",
            defaultValue: 15,
            description:
              "Height of the rotor slot for the lower cage in millimeters.",
            type: "number",
            visibleWhen: {
              all: [
                { field: "rotor_type", equals: "Squirrel Cage" },
                { field: "cage_type", equals: "Double Cage" },
                {
                  field: "rotor_slot_shape_lower_cage",
                  in: ["Rectangle", "Trapezoidal", "Drop profile", "Pear"],
                },
              ],
            },
          },
          {
            parameter: "Slot diameter lower cage [mm]",
            key: "rotor_slot_diameter_lower",
            defaultValue: 10,
            description:
              "Diameter of the rotor slot (Circular shape) for the lower cage in millimeters.",
            type: "number",
            visibleWhen: {
              all: [
                { field: "rotor_type", equals: "Squirrel Cage" },
                { field: "cage_type", equals: "Double Cage" },
                { field: "rotor_slot_shape_lower_cage", equals: "Circular" },
              ],
            },
          },
          {
            parameter: "End-ring height lower cage [mm]",
            key: "end_ring_height_lower",
            defaultValue: 21,
            description:
              "Height of the rotor end-ring for the lower cage in millimeters.",
            type: "number",
            visibleWhen: {
              all: [
                { field: "rotor_type", equals: "Squirrel Cage" },
                { field: "cage_type", equals: "Double Cage" },
                { field: "ring_topology", equals: "Separate Ring" },
              ],
            },
          },
          {
            parameter: "End-ring width lower cage [mm]",
            key: "end_ring_width_lower",
            defaultValue: 20,
            description:
              "Width of the rotor end-ring for the lower cage in millimeters.",
            type: "number",
            visibleWhen: {
              all: [
                { field: "rotor_type", equals: "Squirrel Cage" },
                { field: "cage_type", equals: "Double Cage" },
                { field: "ring_topology", equals: "Separate Ring" },
              ],
            },
          },
          {
            parameter: "End-ring extension lower cage [mm]",
            key: "end_ring_extension_lower",
            defaultValue: 0,
            description:
              "Extension of the rotor end-ring for the lower cage in millimeters.",
            type: "number",
            visibleWhen: {
              all: [
                { field: "rotor_type", equals: "Squirrel Cage" },
                { field: "cage_type", equals: "Double Cage" },
                { field: "ring_topology", equals: "Separate Ring" },
              ],
            },
          },
        ],
      },

      {
        name: "Rotor Winding Specification",
        parameters: [
          // ✅ Wound Field winding params (show only when wound field)
          {
            parameter: "Number of layers",
            key: "rotor_winding_layer",
            defaultValue: "1",
            description: "Number of winding layers.",
            type: "option",
            options: ["1", "2"],
            visibleWhen: { field: "rotor_type", equals: "Wound Field" },
          },
          {
            parameter: "Turns per coil",
            key: "rotor_turns_per_coil",
            defaultValue: 10,
            description: "Number of turns per winding coil.",
            type: "number",
            visibleWhen: { field: "rotor_type", equals: "Wound Field" },
          },
          {
            parameter: "Number of parallel strands",
            key: "rotor_number_of_parallel_strands",
            defaultValue: 1,
            description: "Number of parallel strands in the winding.",
            type: "number",
            visibleWhen: { field: "rotor_type", equals: "Wound Field" },
          },
          {
            parameter: "Rotor slot copper fill factor",
            key: "rotor_slot_copper_fill_factor",
            defaultValue: 0.45,
            description:
              "Copper fill factor of the Wound Field slot winding.",
            type: "number",
            visibleWhen: { field: "rotor_type", equals: "Wound Field" },
          },
          {
            parameter: "Winding parallel paths",
            key: "rotor_parallel_paths",
            defaultValue: 1,
            description:
              "Number of parallel paths in the Wound Field winding.",
            type: "number",
            visibleWhen: { field: "rotor_type", equals: "Wound Field" },
          },
          {
            parameter: "Winding end length [mm]",
            key: "rotor_winding_end_length",
            defaultValue: 80,
            description:
              "Length of the winding end connections in millimeters.",
            type: "number",
            visibleWhen: { field: "rotor_type", equals: "Wound Field" },
          },
          {
            parameter: "Rotor external resistance [Ω]",
            key: "rotor_external_resistance",
            defaultValue: 0.0,
            description: "External resistance of the Wound Rotor in ohms.",
            type: "number",
            visibleWhen: { field: "rotor_type", equals: "Wound Field" },
          },
          {
            parameter: "Winding material",
            key: "rotor_winding_material_wound",
            defaultValue: "Copper",
            description: "Material of the winding (Aluminium or Copper).",
            type: "option",
            options: ["Aluminium", "Copper"],
            visibleWhen: { field: "rotor_type", equals: "Wound Field" },
          },
          {
            parameter: "Winding temperature [°C]",
            key: "rotor_winding_temperature",
            defaultValue: 40.0,
            description: "Temperature of the rotor winding in °C.",
            type: "number",
            visibleWhen: { field: "rotor_type", equals: "Wound Field" },
          },

          // ✅ SQUIRREL CAGE winding temp (always for squirrel cage, both single/double)
          {
            parameter: "Cage temperature [°C]",
            key: "cage_temperature",
            defaultValue: 40.0,
            description: "Temperature of the cage in °C.",
            type: "number",
            visibleWhen: { field: "rotor_type", equals: "Squirrel Cage" },
          },

          // ✅ Squirrel Cage: Single Cage material shown only when squirrel + single
          {
            parameter: "Cage material",
            key: "cage_material_single_cage",
            defaultValue: "Aluminium",
            description: "Material of cage (Aluminium or Copper).",
            type: "option",
            options: ["Aluminium", "Copper"],
            visibleWhen: {
              all: [
                { field: "rotor_type", equals: "Squirrel Cage" },
                { field: "cage_type", equals: "Single Cage" },
              ],
            },
          },

          // ✅ Squirrel Cage: Double Cage -> show upper + lower materials
          {
            parameter: "Cage material",
            key: "cage_material_double_cage", // Check in backend
            defaultValue: "Aluminium",
            description: "Material of cage (Aluminium or Copper).",
            type: "option",
            options: ["Aluminium", "Copper"],
            visibleWhen: {
              all: [
                { field: "rotor_type", equals: "Squirrel Cage" },
                { field: "cage_type", equals: "Double Cage" },
                { field: "ring_topology", equals: "Common Ring" },
              ],
            },
          },
          {
            parameter: "Cage material upper cage",
            key: "cage_material_upper",
            defaultValue: "Aluminium",
            description: "Material of cage upper (Aluminium or Copper).",
            type: "option",
            options: ["Aluminium", "Copper"],
            visibleWhen: {
              all: [
                { field: "rotor_type", equals: "Squirrel Cage" },
                { field: "cage_type", equals: "Double Cage" },
                { field: "ring_topology", equals: "Separate Ring" },
              ],
            },
          },
          {
            parameter: "Cage material lower cage",
            key: "cage_material_lower",
            defaultValue: "Aluminium",
            description: "Material of cage lower (Aluminium or Copper).",
            type: "option",
            options: ["Aluminium", "Copper"],
            visibleWhen: {
              all: [
                { field: "rotor_type", equals: "Squirrel Cage" },
                { field: "cage_type", equals: "Double Cage" },
                { field: "ring_topology", equals: "Separate Ring" },
              ],
            },
          },
        ],
      },
    ],
    description:
      "Analyzes induction machines by estimating equivalent circuit parameters from geometrical data. Ideal for design, analysis, and educational purposes, providing insights without experimental testing.",
  },

  // -----------------------------
  // Other topologies: only stator slot-shape rules applied
  // -----------------------------

  // ✅ Synchronous Reluctance Machine
  {
    topology: "Synchronous Reluctance Machine",
    groups: [
      {
        name: "Model Accuracy",
        parameters: [
          {
            parameter: "Accuracy",
            key: "accuracy_definition",
            defaultValue: "Fast",
            description:
              "Defines how accuracy/fast is going to be the model.",
            type: "option",
            options: ["Fast"],
            // options: ["Fast", "Normal", "Accurate"],
          },
        ],
      },
      {
        name: "Supply Specification",
        parameters: [
          {
            parameter: "Phase number",
            key: "phase_number",
            defaultValue: 3,
            description: "Number of phases of the machine.",
            type: "number",
          },
          {
            parameter: "Current definition",
            key: "current_definition",
            defaultValue: "RMS",
            description:
              "Defines whether the supply current is given as RMS or Peak.",
            type: "option",
            options: ["RMS", "Peak"],
          },
          {
            parameter: "Winding connection",
            key: "winding_connection",
            defaultValue: "Star",
            description: "Selects the winding configuration (Star or Delta).",
            type: "option",
            options: ["Star", "Delta"],
          },
          {
            parameter: "Supply current [A]",
            key: "supply_current",
            defaultValue: 10.0,
            description: "Electrical supply current in amperes.",
            type: "number",
          },
          {
            parameter: "Supply frequency [Hz]",
            key: "supply_frequency",
            defaultValue: 50.0,
            description: "Electrical supply frequency in hertz.",
            type: "number",
          },
          {
            parameter: "Advance angle [deg]",
            key: "advance_angle_deg",
            defaultValue: 45.0,
            description: "Advance angle of the stator current in electrical degree.",
            type: "number",
          },
        ],
      },
      {
        name: "Stator Specification",
        parameters: [
          {
            parameter: "Number of slots",
            key: "number_of_stator_slots",
            defaultValue: 36,
            description: "Total number of stator slots.",
            type: "number",
          },
          {
            parameter: "Number of pole pairs",
            key: "number_of_pole_pairs",
            defaultValue: 2,
            description:
              "Number of machine pole pairs (half the total pole count).",
            type: "number",
          },
          {
            parameter: "Outer diameter [mm]",
            key: "stator_outer_diameter",
            defaultValue: 300.0,
            description: "Outer diameter of the stator in millimeters.",
            type: "number",
          },
          {
            parameter: "Stator inner diameter [mm]",
            key: "air_gap_diameter",
            defaultValue: 200.0,
            description: "Stator inner diameter in millimeters.",
            type: "number",
          },
          {
            parameter: "Stack length [mm]",
            key: "stack_length",
            defaultValue: 150,
            description:
              "Axial length of the stator/rotor stack in millimeters.",
            type: "number",
          },

          // ✅ slot shape selector
          {
            parameter: "Slot shape",
            key: "stator_slot_shape",
            defaultValue: "Rectangle",
            description: "Shape of the stator slots.",
            type: "option",
            options: ["Rectangle", "Trapezoidal", "Drop profile"],
          },

          // ✅ RULES
          // Rectangle -> show slot width; hide top/bottom
          // Trapezoidal & Drop profile -> show top/bottom; hide slot width
          {
            parameter: "Slot width [mm]",
            key: "stator_slot_width",
            defaultValue: 10,
            description: "Width of the stator slot in millimeters.",
            type: "number",
            visibleWhen: { field: "stator_slot_shape", equals: "Rectangle" },
          },
          {
            parameter: "Slot width (top) [mm]",
            key: "stator_slot_width_top",
            defaultValue: 10,
            description: "Top width of the stator slot in millimeters.",
            type: "number",
            visibleWhen: {
              field: "stator_slot_shape",
              in: ["Trapezoidal", "Drop profile"],
            },
          },
          {
            parameter: "Slot width (bottom) [mm]",
            key: "stator_slot_width_bottom",
            defaultValue: 8,
            description: "Bottom width of the stator slot in millimeters.",
            type: "number",
            visibleWhen: {
              field: "stator_slot_shape",
              in: ["Trapezoidal", "Drop profile"],
            },
          },

          {
            parameter: "Slot height [mm]",
            key: "stator_slot_height",
            defaultValue: 17,
            description: "Height of the stator slot in millimeters.",
            type: "number",
          },
          {
            parameter: "Slot opening height [mm]",
            key: "stator_slot_opening_height",
            defaultValue: 0.96,
            description: "Height of the stator slot opening in millimeters.",
            type: "number",
          },
          {
            parameter: "Tooth tip angle [deg]",
            key: "stator_tooth_tip_angle",
            defaultValue: 20.0,
            description: "Angle of the stator tooth tip in degrees.",
            type: "number",
          },
          {
            parameter: "Slot opening width [mm]",
            key: "stator_slot_opening_width",
            defaultValue: 3,
            description: "Width of the stator slot opening in millimeters.",
            type: "number",
          },
          {
            parameter: "Stator skew",
            key: "stator_skew",
            defaultValue: false,
            description: "Indicates whether the stator is skewed.",
            type: "boolean",
          },
        ],
      },
      {
        name: "Stator Winding Specification",
        parameters: [
          {
            parameter: "Number of layers",
            key: "stator_winding_layer",
            defaultValue: "1",
            description: "Number of winding layers.",
            type: "option",
            options: ["1", "2"],
          },
          {
            parameter: "Turns per coil",
            key: "stator_turns_per_coil",
            defaultValue: 10,
            description: "Number of turns per winding coil.",
            type: "number",
          },
          {
            parameter: "Number of parallel strands",
            key: "stator_number_of_parallel_strands",
            defaultValue: 1,
            description: "Number of parallel strands in the winding.",
            type: "number",
          },
          {
            parameter: "Stator slot copper fill factor",
            key: "stator_slot_copper_fill_factor",
            defaultValue: 0.45,
            description: "Copper fill factor of the stator slot winding.",
            type: "number",
          },
          {
            parameter: "Winding parallel paths",
            key: "stator_winding_parallel_paths",
            defaultValue: 1,
            description: "Number of parallel paths in the stator winding.",
            type: "number",
          },
          {
            parameter: "Winding end length [mm]",
            key: "stator_winding_end_length",
            defaultValue: 80,
            description:
              "Length of the winding end connections in millimeters.",
            type: "number",
          },
          {
            parameter: "Coil Throw",
            key: "coil_throw",
            // defaultValue: 1,
            description:
              "The coil throw, or span, indicating how many slots a coil spans. This value is automatically calculated but can be manually adjusted within its maximum determined value.",
            // type: "number",
            type: "computed",
            dependsOn: ["number_of_stator_slots", "number_of_pole_pairs"],
            // formula: (p: Record<string, number>) =>
            //   Math.round(p["number_of_stator_slots"] / (2 * p["number_of_pole_pairs"])),
          },
          {
            parameter: "Winding temperature [°C]",
            key: "stator_winding_temperature",
            defaultValue: 40.0,
            description: "Temperature of the stator winding in °C.",
            type: "number",
          },
          {
            parameter: "Winding material",
            key: "stator_winding_material",
            defaultValue: "Copper",
            description: "Material of the winding (Aluminium or Copper).",
            type: "option",
            options: ["Aluminium", "Copper"],
          },
          {
            parameter: "Winding type",
            key: "winding_type",
            defaultValue: "Distributed",
            description: "Type of winding arrangement.",
            type: "option",
            options: ["Distributed"],
          },
        ],
      },
      {
        name: "Rotor Specification",
        parameters: [
          {
            parameter: "Outer diameter [mm]",
            key: "rotor_outer_diameter",
            defaultValue: 199,
            description: "Rotor outer diameter in millimeters.",
            type: "number",
          },
          {
            parameter: "Shaft diameter [mm]",
            key: "rotor_inner_diameter",
            defaultValue: 50,
            description: "Diameter of the rotor shaft in millimeters.",
            type: "number",
          },
          // ✅ Flux barrier parameters, Check it
          {
            parameter: "Number of flux barriers",
            key: "number_of_rotor_flux_barriers",
            defaultValue: "3",
            description:
              "The number of rotor flux barrier (no greater than 6).",
            type: "option",
            options: ["1", "2", "3", "4", "5", "6"],
          },
          {
            parameter: "Flux barrier shape",
            key: "shape_of_the_rotor_flux_barrier",
            defaultValue: "Rectangular",
            description:
              "The shape of the rotor flux barrier.",
            type: "option",
            options: ["Rectangular", "Joukowski"],
          },
          {
            parameter: "Insulation ratio",
            key: "insulation_ratio",
            defaultValue: 0.45,
            description:
              "The proportion of air to steel in the rotor.",
            type: "number",
          },
          {
            parameter: "Iron tangential bridge width [mm]",
            key: "iron_tangential_bridge_width",
            defaultValue: 1,
            description:
              "Width of the iron bridge at the end of the rotor flux barrier in millimeters.",
            type: "number",
          },
        ],
      },
    ],
    description:
      "This tool analyzes synchronous reluctance machines using an analytical model based on magnetic potential equations. By inputting the machine’s geometric and material data, users can estimate key performance indicators such as torque, flux distribution, and inductance.",
  },

  // ✅ Surface Mounted Permanent Magnet Machine
  {
    topology: "Surface Mounted Permanent Magnet Machine",
    groups: [
      {
        name: "Model Accuracy",
        parameters: [
          {
            parameter: "Accuracy",
            key: "accuracy_definition",
            defaultValue: "Fast",
            description:
              "Defines how accuracy/fast is going to be the model.",
            type: "option",
            options: ["Fast"],
            // options: ["Fast", "Normal", "Accurate"],
          },
        ],
      },
      {
        name: "Supply Specification",
        parameters: [
          {
            parameter: "Open Circuit",
            key: "open_circuit",
            defaultValue: true,
            description: "Whether the machine is open circuit or on load.",
            type: "boolean",
          },
          {
            parameter: "Phase number",
            key: "phase_number",
            defaultValue: 3,
            description: "Number of phases of the machine.",
            type: "number",
          },
          {
            parameter: "Current definition",
            key: "current_definition",
            defaultValue: "RMS",
            description:
              "Defines whether the supply current is given as RMS or Peak.",
            type: "option",
            options: ["RMS", "Peak"],
            // visibleWhen: {
            //   all: [
            //     { field: "open_circuit", equals: false },
            //   ],
            // },
          },
          {
            parameter: "Winding connection",
            key: "winding_connection",
            defaultValue: "Star",
            description: "Selects the winding configuration (Star or Delta).",
            type: "option",
            options: ["Star", "Delta"],
          },
          {
            parameter: "Supply current [A]",
            key: "supply_current",
            defaultValue: 10.0,
            description: "Electrical supply current in amperes.",
            type: "number",
            // visibleWhen: {
            //   all: [
            //     { field: "open_circuit", equals: false },
            //   ],
            // },
          },
          {
            parameter: "Supply frequency [Hz]",
            key: "supply_frequency",
            defaultValue: 50.0,
            description: "Electrical supply frequency in hertz.",
            type: "number",
          },
          {
            parameter: "Advance angle [deg]",
            key: "advance_angle_deg",
            defaultValue: 45.0,
            description: "Advance angle of the stator current in electrical degree.",
            type: "number",
            // visibleWhen: {
            //   all: [
            //     { field: "open_circuit", equals: false },
            //   ],
            // },
          },
        ],
      },
      {
        name: "Stator Specification",
        parameters: [
          {
            parameter: "Number of slots",
            key: "number_of_stator_slots",
            defaultValue: 12,
            description: "Total number of stator slots.",
            type: "number",
          },
          {
            parameter: "Number of pole pairs",
            key: "number_of_pole_pairs",
            defaultValue: 5,
            description:
              "Number of machine pole pairs (half the total pole count).",
            type: "number",
          },
          {
            parameter: "Outer diameter [mm]",
            key: "stator_outer_diameter",
            defaultValue: 160.0,
            description: "Outer diameter of the stator in millimeters.",
            type: "number",
          },
          {
            parameter: "Stator inner diameter [mm]",
            key: "air_gap_diameter",
            defaultValue: 90.0,
            description: "Stator inner diameter in millimeters.",
            type: "number",
          },
          {
            parameter: "Stack length [mm]",
            key: "stack_length",
            defaultValue: 150,
            description:
              "Axial length of the stator/rotor stack in millimeters.",
            type: "number",
          },

          // ✅ slot shape selector
          {
            parameter: "Slot shape",
            key: "stator_slot_shape",
            defaultValue: "Rectangle",
            description: "Shape of the stator slots.",
            type: "option",
            options: ["Rectangle", "Trapezoidal", "Drop profile"],
          },

          // ✅ RULES
          // Rectangle -> show slot width; hide top/bottom
          // Trapezoidal & Drop profile -> show top/bottom; hide slot width
          {
            parameter: "Slot width [mm]",
            key: "stator_slot_width",
            defaultValue: 10,
            description: "Width of the stator slot in millimeters.",
            type: "number",
            visibleWhen: { field: "stator_slot_shape", equals: "Rectangle" },
          },
          {
            parameter: "Slot width (top) [mm]",
            key: "stator_slot_width_top",
            defaultValue: 10,
            description: "Top width of the stator slot in millimeters.",
            type: "number",
            visibleWhen: {
              field: "stator_slot_shape",
              in: ["Trapezoidal", "Drop profile"],
            },
          },
          {
            parameter: "Slot width (bottom) [mm]",
            key: "stator_slot_width_bottom",
            defaultValue: 8,
            description: "Bottom width of the stator slot in millimeters.",
            type: "number",
            visibleWhen: {
              field: "stator_slot_shape",
              in: ["Trapezoidal", "Drop profile"],
            },
          },

          {
            parameter: "Slot height [mm]",
            key: "stator_slot_height",
            defaultValue: 17,
            description: "Height of the stator slot in millimeters.",
            type: "number",
          },
          {
            parameter: "Slot opening height [mm]",
            key: "stator_slot_opening_height",
            defaultValue: 0.96,
            description: "Height of the stator slot opening in millimeters.",
            type: "number",
          },
          {
            parameter: "Tooth tip angle [deg]",
            key: "stator_tooth_tip_angle",
            defaultValue: 20.0,
            description: "Angle of the stator tooth tip in degrees.",
            type: "number",
          },
          {
            parameter: "Slot opening width [mm]",
            key: "stator_slot_opening_width",
            defaultValue: 3,
            description: "Width of the stator slot opening in millimeters.",
            type: "number",
          },
          {
            parameter: "Stator skew",
            key: "stator_skew",
            defaultValue: false,
            description: "Indicates whether the stator is skewed.",
            type: "boolean",
          },
        ],
      },
      {
        name: "Stator Winding Specification",
        parameters: [
          {
            parameter: "Number of layers",
            key: "stator_winding_layer",
            defaultValue: "1",
            description: "Number of winding layers.",
            type: "option",
            options: ["1", "2"],
          },
          {
            parameter: "Turns per coil",
            key: "stator_turns_per_coil",
            defaultValue: 10,
            description: "Number of turns per winding coil.",
            type: "number",
          },
          {
            parameter: "Number of parallel strands",
            key: "stator_number_of_parallel_strands",
            defaultValue: 1,
            description: "Number of parallel strands in the winding.",
            type: "number",
          },
          {
            parameter: "Stator slot copper fill factor",
            key: "stator_slot_copper_fill_factor",
            defaultValue: 0.45,
            description: "Copper fill factor of the stator slot winding.",
            type: "number",
          },
          {
            parameter: "Winding parallel paths",
            key: "stator_winding_parallel_paths",
            defaultValue: 1,
            description: "Number of parallel paths in the stator winding.",
            type: "number",
          },
          {
            parameter: "Winding end length [mm]",
            key: "stator_winding_end_length",
            defaultValue: 80,
            description:
              "Length of the winding end connections in millimeters.",
            type: "number",
          },
          {
            parameter: "Coil Throw",
            key: "coil_throw",
            defaultValue: 1,
            description:
              "The coil throw, or span, indicating how many slots a coil spans. This value is automatically calculated but can be manually adjusted within its maximum determined value.",
            type: "number",
            valueWhen: {
              when: { field: "winding_type", equals: "Tooth coil" },
              value: 1
            },
          },
          {
            parameter: "Winding temperature [°C]",
            key: "stator_winding_temperature",
            defaultValue: 40.0,
            description: "Temperature of the stator winding in °C.",
            type: "number",
          },
          {
            parameter: "Winding material",
            key: "stator_winding_material",
            defaultValue: "Copper",
            description: "Material of the winding (Aluminium or Copper).",
            type: "option",
            options: ["Aluminium", "Copper"],
          },
          {
            parameter: "Winding type",
            key: "winding_type",
            defaultValue: "Tooth coil",
            description: "Type of winding arrangement.",
            type: "option",
            options: ["Tooth coil"],
          },
        ],
      },
      {
        name: "Rotor Specification",
        parameters: [
          {
            parameter: "Outer diameter [mm]",
            key: "rotor_outer_diameter",
            defaultValue: 86,
            description: "Rotor outer diameter in millimeters.",
            type: "number",
          },
          {
            parameter: "Shaft diameter [mm]",
            key: "rotor_inner_diameter",
            defaultValue: 50,
            description: "Diameter of the rotor shaft in millimeters.",
            type: "number",
          },

          {
            parameter: "Magnet Height [mm]",
            key: "magnet_height",
            defaultValue: 4,
            description:
              "The height of the rotor magnet in millimeters.",
            type: "number",
          },
        ],
      },
      {
        name: "Permanent Magnet Specification",
        parameters: [
          {
            parameter: "Magnetization direction",
            key: "magnetization_direction",
            defaultValue: "Radial",
            description: "The direction of magnetization in the permanent magnet.",
            type: "option",
            options: ["Radial", "Parallel"],
          },
          {
            parameter: "Remanence flux density [T]",
            key: "remanence_flux_density",
            defaultValue: 1.12,
            description: "The remanence flux density of the permanent magnet in Tesla.",
            type: "number",
          },

          {
            parameter: "Coercivity [A/m]",
            key: "magnet_coercivity",
            defaultValue: 895246,
            description:
              "The coercivity of the permanent magnet in Amperes per meter.",
            type: "number",
            min: -1e7,
            max: 1e7,
          },
        ],
      },
    ],
    description:
      "This tool analyzes surface-mounted permanent magnet machines using a magnetic equivalent circuit model. By inputting geometric dimensions and material properties, it estimates key performance parameters such as flux linkage, back-EMF, and torque.",
  },
];
